/**
 * In welke onderdelen van de app een rol werkt.
 *
 * Verzoek BuurtKlusBedrijf: 'We zien menu-items en tabbladen waar we niets mee
 * doen.' Hun planner plant, en wil de impactmeet-kant niet in beeld hebben.
 *
 * **Dit is een gordijn, geen slot.** Het bepaalt alleen wat er in het menu en op
 * de tabbladen STAAT. Wie een verborgen pagina alsnog via een link opent, krijgt
 * hem gewoon te zien — dat is met de klant zo afgesproken. Het slot zijn de
 * rechten (`permissionsCatalog`, `.view` / `.edit`); die staan hier volledig los
 * van. Bouw hier dus nooit iets op wat beveiliging moet zijn.
 *
 * De onderdelen zijn dezelfde indeling als waarin de rechten al gegroepeerd
 * staan, zodat er niet twee manieren ontstaan om de app op te delen.
 *
 * AI-CONTEXT: See docs/features/rol-onderdelen.md
 * AI-CONTEXT: See docs/features/role-based-permissions.md
 */

/**
 * @typedef {object} RoleArea
 * @property {string} key - Zoals hij in `UserRoles.Areas` staat
 * @property {string} label - Wat de beheerder leest; zelfde woord als elders in de app
 * @property {string} description - Waar het onderdeel over gaat
 */

/**
 * De onderdelen, in de volgorde waarin ze in het rollenscherm staan.
 *
 * `impactMeten` draagt met opzet dezelfde naam als de module op het
 * instellingenscherm. Twee woorden voor hetzelfde ding laten die twee schermen
 * het oneens zijn over wat er aanstaat.
 *
 * @type {Array<RoleArea>}
 */
const ROLE_AREAS = [
    {
        key: 'ondersteuning',
        label: 'Ondersteuning',
        description: 'Huishoudens, deelnemers, contactmomenten en materialen.',
    },
    {
        key: 'planning',
        label: 'Planning',
        description: 'De agenda, afspraken en het rooster.',
    },
    {
        key: 'impactMeten',
        label: 'Impact meten',
        description: 'Onderzoeken, meetmomenten, analyses en de Theory of Change.',
    },
    {
        key: 'rapportages',
        label: 'Rapportages',
        description: 'Rapportages en impactpagina’s.',
    },
    {
        key: 'portfolio',
        label: 'Portfolio',
        description: 'Het overzicht over meerdere organisaties heen.',
    },
]

/** @type {Array<string>} */
const ROLE_AREA_KEYS = ROLE_AREAS.map((area) => area.key)

/**
 * Wat er opgeslagen wordt als een rol in GEEN enkel onderdeel werkt.
 *
 * Nodig omdat een lege lijst 'alles' betekent (zie `roleWorksIn`): het laatste
 * vinkje uitzetten zou de rol anders juist alles laten zien. Deze sleutel komt
 * niet in `ROLE_AREAS` voor, dus hij matcht nooit een echt onderdeel.
 *
 * Hoort hier en niet in het rollenscherm: de schrijvende en de lezende kant
 * moeten het over deze waarde eens zijn.
 * AI-CONTEXT: See docs/pitfalls.md#dev-mirrored-constant-guarded-only-by-a-comment
 *
 * @type {string}
 */
const NO_AREAS = 'geen'

/** @type {Record<string, RoleArea>} */
const AREA_PER_KEY = ROLE_AREAS.reduce((acc, area) => ({ ...acc, [area.key]: area }), {})

/**
 * De naam van een onderdeel, of de sleutel zelf als we het niet kennen.
 *
 * @param {string} key - Sleutel uit ROLE_AREA_KEYS
 * @returns {string}
 */
const areaLabel = (key) => (AREA_PER_KEY[key] ? AREA_PER_KEY[key].label : String(key || ''))

/**
 * Of deze rol in dit onderdeel werkt.
 *
 * **Een rol die niets heeft gekozen ziet alles.** Dat is hier de veilige kant, en
 * precies andersom dan bij een echte poort: élke bestaande rol bij élke klant
 * heeft straks geen `Areas`, dus zou 'niets gekozen' dichtvallen, dan is op de
 * dag van de release bij iedereen het halve menu weg. Verbergen beschermt hier
 * niets — het maakt alleen rustiger — dus 'nog niet ingesteld' hoort te tonen.
 *
 * Een onderdeel dat wij zelf niet kennen (een tikfout in een aanroep) toont
 * daarom ook gewoon: een verkeerd gespelde sleutel mag geen scherm laten
 * verdwijnen zonder dat iets het zegt.
 *
 * @param {object|null|undefined} role - Document uit UserRoles
 * @param {string} areaKey - Sleutel uit ROLE_AREA_KEYS
 * @returns {boolean}
 */
const roleWorksIn = (role, areaKey) => {
    if (!AREA_PER_KEY[areaKey]) return true

    const areas = role && role.Areas
    if (!Array.isArray(areas) || areas.length === 0) return true

    return areas.includes(areaKey)
}

export { ROLE_AREAS, ROLE_AREA_KEYS, NO_AREAS, areaLabel }
export default roleWorksIn
